import { useId } from 'react';
import { COLOR_PALETTE } from '../../config/color-palette.js';
import styles from './color-field.module.css';

const NO_COLOR = '';

/** What the native picker opens on when nothing is chosen yet: it cannot represent "no colour". */
const PICKER_START = '#1f6feb';

type ColorFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
};

export const ColorField = ({ label, name, value, onChange, hint, error }: ColorFieldProps) => {
  const fieldId = useId();
  const messageId = `${fieldId}-message`;
  const hasError = error !== undefined;
  const message = error ?? hint;
  const isCustom = value !== NO_COLOR && !COLOR_PALETTE.includes(value);

  return (
    <fieldset
      className={styles.group}
      aria-describedby={message === undefined ? undefined : messageId}
      aria-invalid={hasError}
    >
      <legend className={styles.legend}>{label}</legend>
      <div className={styles.swatches}>
        <button
          type="button"
          className={`${styles.swatch} ${styles.none} ${value === NO_COLOR ? styles.selected : ''}`}
          aria-pressed={value === NO_COLOR}
          aria-label="Без кольору"
          onClick={() => {
            onChange(NO_COLOR);
          }}
        >
          —
        </button>
        {COLOR_PALETTE.map((color) => (
          <button
            key={color}
            type="button"
            className={`${styles.swatch} ${value === color ? styles.selected : ''}`}
            style={{ background: color }}
            aria-pressed={value === color}
            aria-label={color}
            onClick={() => {
              onChange(color);
            }}
          />
        ))}
        <label
          className={`${styles.swatch} ${styles.custom} ${isCustom ? styles.selected : ''}`}
          style={isCustom ? { background: value } : undefined}
        >
          <input
            type="color"
            name={name}
            className={styles.customInput}
            value={value === NO_COLOR ? PICKER_START : value}
            aria-label="Свій колір"
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
          {!isCustom && <span aria-hidden="true">+</span>}
        </label>
      </div>
      {message !== undefined && (
        <p
          id={messageId}
          className={hasError ? styles.error : styles.hint}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
    </fieldset>
  );
};
